import { useEffect, useState } from "react";

// setTimeout fires at once for delays past a signed 32-bit int (~24.8 days).
const MAX_TIMEOUT_MS = 2_147_483_647;

/**
 * The current time, as of the last boundary passed. Re-renders the caller
 * when the next of `boundaries` (epoch ms) arrives, and only then, so a page
 * left open turns over on time without ticking every second. A boundary more
 * than ~24.8 days out takes several capped timeouts to reach; each one that
 * fires early schedules the next.
 */
export const useBoundaryClock = (boundaries: number[]) => {
  const [now, setNow] = useState(Date.now);
  const next = boundaries.reduce(
    (soonest, b) => (b > now && b < soonest ? b : soonest),
    Infinity,
  );
  useEffect(() => {
    if (next === Infinity) return;
    let id: number | undefined;
    const schedule = () => {
      const wait = next - Date.now();
      if (wait <= 0) {
        setNow(Date.now());
        return;
      }
      id = window.setTimeout(schedule, Math.min(wait, MAX_TIMEOUT_MS));
    };
    schedule();
    return () => window.clearTimeout(id);
  }, [next]);
  return now;
};
